// ============================================
// Path: /frontend/src/pages/AgentsPage.jsx
// Purpose: Display all registered real estate agents with subscription tiers
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { AgentCard, AgentCardSkeleton } from '../components/AgentCard';
import { usersAPI } from '../api/users.api';
import { User } from 'lucide-react';

// ========================================
// MAIN COMPONENT
// ========================================
export default function AgentsPage() {
  console.log('🎨 [AgentsPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchAgents = async () => {
      console.log('🔵 [AgentsPage] Fetching agents with subscription tiers...');
      setLoading(true);
      
      try {
        const data = await usersAPI.getAllAgents({
          subscriptionTier: 'BRONZE,SILVER,GOLD,PLATINUM'
        });
        console.log('✅ [AgentsPage] Agents data received:', data);
        setAgents(data.users || []);
        setError(null);
      } catch (err) {
        console.error('❌ [AgentsPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load agents');
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-12">
        <div className="text-left mb-12">
          <h1 className="text-4xl font-bold">Real Estate Agents</h1>
          <p className="text-lg text-muted-foreground">
            Browse all of our registered real estate agents.
          </p>
        </div>

        {/* Error State */}
        {error && (
          <p className="text-destructive text-center">
            Error: {error}
          </p>
        )}

        {/* Agents Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {loading && 
            Array.from({ length: 12 }).map((_, i) => (
              <AgentCardSkeleton key={i} />
            ))
          }
          {!loading && agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agentId={agent.id}
              name={agent.fullName}
              email={agent.email}
            />
          ))}
        </div>

        {/* Empty State */}
        {!loading && agents.length === 0 && !error && (
          <div className="text-center py-16 text-muted-foreground">
            <User className="w-12 h-12 mx-auto mb-4" />
            <p>No agents have registered yet.</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}